/**
 * Runtime configuration for the Portal application.
 *
 * In local environments, config is loaded from import.meta.env (environment variables).
 * In cloud environments, config is loaded from /config.json at runtime.
 */

export interface AppConfig {
	apiUrl: string;
	auth0Domain: string;
	auth0ClientId: string;
	auth0Audience: string;
	sentryDsn: string;
	sentryEnv: string;
	sentryRelease: string;
	googleAddressKey: string;
	klipyKey: string;
	amplitudeApiKey: string;
	environment: "local" | "qa" | "demo" | "prod";
	tanstackQueryDevtools: boolean;
}

let configCache: AppConfig | null = null;
let configPromise: Promise<AppConfig> | null = null;

function loadConfigFromEnv(): AppConfig {
	return {
		apiUrl: import.meta.env.VITE_API_URL ?? "http://localhost:8080",
		auth0Domain: import.meta.env.VITE_AUTH0_DOMAIN ?? "",
		auth0ClientId: import.meta.env.VITE_AUTH0_CLIENT_ID ?? "",
		auth0Audience: import.meta.env.VITE_AUTH0_AUDIENCE ?? "",
		sentryDsn: import.meta.env.VITE_SENTRY_DSN ?? "",
		sentryEnv: import.meta.env.VITE_SENTRY_ENV ?? "local",
		sentryRelease: import.meta.env.VITE_SENTRY_RELEASE ?? "local",
		googleAddressKey: import.meta.env.VITE_GOOGLE_ADDRESS_VERIFICATION_KEY ?? "",
		klipyKey: import.meta.env.VITE_KLIPY_KEY ?? "",
		amplitudeApiKey: import.meta.env.VITE_AMPLITUDE_API_KEY ?? "",
		environment: (import.meta.env.VITE_PORTAL_ENV as AppConfig["environment"]) ?? "local",
		tanstackQueryDevtools: import.meta.env.VITE_TANSTACK_QUERY_DEVTOOLS === "true",
	};
}

async function loadConfigFromJson(): Promise<AppConfig> {
	const response = await fetch("/config.json");
	if (!response.ok) {
		throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
	}
	const json = await response.json();

	return {
		apiUrl: json.apiUrl,
		auth0Domain: json.auth0Domain,
		auth0ClientId: json.auth0ClientId,
		auth0Audience: json.auth0Audience,
		sentryDsn: json.sentryDsn ?? "",
		sentryEnv: json.sentryEnv ?? json.environment ?? "prod",
		sentryRelease: json.sentryRelease ?? "",
		googleAddressKey: json.googleAddressKey,
		klipyKey: json.klipyKey ?? "",
		amplitudeApiKey: json.amplitudeApiKey ?? "",
		environment: json.environment ?? "prod",
		tanstackQueryDevtools: false,
	};
}

/**
 * Load the application configuration.
 * Returns cached config if already loaded.
 *
 * In local environments, loads from environment variables.
 * In cloud environments, loads from /config.json at runtime.
 */
export async function loadConfig(): Promise<AppConfig> {
	if (configCache) {
		return configCache;
	}

	if (configPromise) {
		return configPromise;
	}

	if (import.meta.env.DEV) {
		configCache = loadConfigFromEnv();
		return configCache;
	}

	configPromise = loadConfigFromJson().then((config) => {
		configCache = config;
		configPromise = null;
		return config;
	});

	return configPromise;
}

/**
 * Get the configuration synchronously.
 * Loads from env vars if not already cached.
 */
export function getConfig(): AppConfig {
	if (!configCache) {
		if (import.meta.env.DEV) {
			configCache = loadConfigFromEnv();
			return configCache;
		}
		throw new Error(
			"Config not loaded. Call loadConfig() during app initialization before accessing getConfig().",
		);
	}
	return configCache;
}

/**
 * Check if config has been loaded.
 */
export function isConfigLoaded(): boolean {
	return configCache !== null;
}
